import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import brandbookService from '../../services/brandbookApi';
import { BrandbookSectionType } from '../../types';
import useIsMobile from 'hooks/useIsMobile';

// Props for the RegenerateModal component
interface RegenerateModalProps {
  open: boolean;                   // Controls modal visibility
  onOpenChange: (open: boolean) => void;  // Handler for opening/closing the modal
  title: string;                   // Title of the section being regenerated
  section:                         // Section identifier
    | 'Slogan'
    | 'Brand Values'
    | 'Vision'
    | 'Voice'
    | 'Product/Service'
    | 'Color Palette'
    | 'Logo';
  brandBook: any;                  // Current brandbook data
  onRegenerate?: (newContent: any) => void;  // Handler for regenerated content
  position?: { top: number; left: number };  // Position for the modal
}

// Maps UI section names to API section names
function sectionToApiFormat(section: RegenerateModalProps['section']): BrandbookSectionType {
  const mapping: Record<RegenerateModalProps['section'], BrandbookSectionType> = {
    'Slogan': 'slogan',
    'Brand Values': 'brandValues',
    'Vision': 'vision',
    'Voice': 'voice',
    'Product/Service': 'productService',
    'Color Palette': 'colorPalette',
    'Logo': 'logo'
  };
  return mapping[section];
}

// Modal component for regenerating section content
function RegenerateModal({
  open,
  onOpenChange,
  title,
  section,
  brandBook,
  onRegenerate,
  position,
}: RegenerateModalProps) {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  // Calculate modal position - improved for mobile
  const modalStyle = isMobile 
    ? {
        position: 'fixed' as 'fixed',
        top: '30%', // Position in the upper part of the screen
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: '90%',
        zIndex: 10000,
      } 
    : position 
      ? {
          position: 'absolute' as 'absolute',
          top: `${position.top}px`,
          left: `${position.left - 150}px`,
          transform: 'none',
          width: '300px',
          maxWidth: '300px'
        } 
      : {};

  // Handle regenerate button click
  const handleRegenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setIsLoading(true);
    try {
      // Special handling for logo regeneration
      if (section === 'Logo') {
        // Call the dedicated logo regeneration API
        const response = await brandbookService.regenerateLogo(
          brandBook,
          prompt.trim()
        );

        if (response.success) {
          // Format the logo data correctly for the parent component
          // The API returns logo_url as an array, but the component expects a string
          const logoData = { logo_url: response.data.logo_url[0] };
          onRegenerate?.(logoData);
          onOpenChange(false);
          setPrompt('');
        } else {
          toast.error(response.message || 'Failed to regenerate logo');
        }
      } else {
        // For all other sections, use the regular regenerate API
        const response = await brandbookService.regenerateSection({
          brandBook: brandBook,
          section: sectionToApiFormat(section),
          prompt: prompt.trim(),
        });

        if (response.success) {
          // Pass regenerated content to parent component
          onRegenerate?.(response.data.regenerated_section);
          onOpenChange(false);
          setPrompt('');
        } else {
          toast.error(response.message || 'Failed to regenerate content');
        }
      }
    } catch (error) {
      console.error('Failed to regenerate:', error);
      toast.error('Failed to regenerate content');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9999]" />
          <Dialog.Content 
            className="fixed text-text-primary bg-background rounded-lg shadow-lg p-4 flex flex-col gap-1 z-[10000]"
            style={modalStyle}
          >
            <Dialog.Title className="text-lg text-left font-bold text-primary">
              Regenerate {title.toLowerCase()}
            </Dialog.Title>
            <div className="flex flex-col mt-2 w-full gap-2">
              <div className="flex flex-col">
                <label className="flex flex-col font-normal text-left text-sm">
                  <Input
                    className="rounded-md mt-1 text-sm"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Enter prompt here"
                  />
                  <span className="text-xs mt-1">Eg. Make it funny</span>
                </label>
              </div>
            </div>
            <div className="flex flex-row gap-2 mt-3 items-center justify-end">
              <Button
                variant="customSecondary"
                onClick={() => onOpenChange(false)}
                className="text-xs bg-secondary"
                size="sm"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="customSecondary"
                onClick={handleRegenerate}
                className="text-xs bg-secondary"
                size="sm"
                disabled={isLoading}
              >
                {isLoading ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default RegenerateModal;
